import React, {
  createContext,
  useEffect,
  useMemo,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import { noop } from 'lodash';
import useCurrentTenant from 'infrastructure/currentTenant/useCurrentTenant';
import useGetAllSettings from 'slices/SettingsV2/hooks/useGetAllSettings';

const TENANT_FONT_STYLE_TAG_ID = 'tenant-fonts';
const FALLBACK_FONTS =
  "-apple-system, BlinkMacSystemFont, 'Inter var', 'Inter', Arial, sans-serif";

const normalizeFontWeight = (fontWeight: unknown) => {
  if (fontWeight === undefined || fontWeight === null) {
    return '';
  }

  const normalizedFontWeight = String(fontWeight).trim().toLowerCase();

  if (!normalizedFontWeight) {
    return '';
  }

  if (normalizedFontWeight === 'normal' || normalizedFontWeight === 'bold') {
    return normalizedFontWeight;
  }

  if (!/^\d+$/.test(normalizedFontWeight)) {
    return '';
  }

  const numericFontWeight = Number(normalizedFontWeight);

  return numericFontWeight >= 1 && numericFontWeight <= 1000
    ? normalizedFontWeight
    : '';
};

type CurrentTenantFontContextType = {
  isLoadingTenantFont: boolean;
  loadTenantFont: () => void;
};

export const CurrentTenantFontContext =
  createContext<CurrentTenantFontContextType>({
    isLoadingTenantFont: false,
    loadTenantFont: () => {},
  });

function CurrentTenantFontProvider({
  children,
  loadTenantSettings = true,
}: {
  children: ReactNode;
  loadTenantSettings?: boolean;
}) {
  const [isLoadingTenantFont, setIsLoadingTenantFont] = useState(false);
  const {
    currentTenant: { name: tenantName },
  } = useCurrentTenant();

  const { data: loadedSettings } = useGetAllSettings(noop, {
    enabled: loadTenantSettings,
  });
  const settings = loadTenantSettings ? loadedSettings : undefined;
  const engagementUnitFont = settings?.engagementUnitSettings?.defaultFontUrl;
  const fontFamily =
    typeof engagementUnitFont === 'string' ? engagementUnitFont.trim() : '';
  const fontSize = settings?.engagementUnitSettings?.defaultFontSize;
  const fontWeight = normalizeFontWeight(
    settings?.engagementUnitSettings?.defaultFontWeight
  );

  useEffect(() => {
    const existingStyleTag = document.querySelector(
      `style#${TENANT_FONT_STYLE_TAG_ID}`
    );

    if (!fontFamily && !fontSize && !fontWeight) {
      existingStyleTag?.remove();
      return;
    }

    const fontName = fontFamily.split('.')[0];

    let css = '';

    if (fontSize) {
      css += `
      :root {
        --engagement-unit-font-size: ${fontSize}px;
      }`;
    }

    if (fontWeight) {
      css += `
      :root {
        --engagement-unit-font-weight: ${fontWeight};
      }`;
    }

    if (fontFamily) {
      let fontFormat = 'opentype';
      let fontUrl = fontFamily;
      const fontFaceWeight = fontWeight || '500';

      if (fontFamily.endsWith('.woff2')) {
        fontFormat = 'woff2';
      } else if (fontFamily.endsWith('.woff')) {
        fontFormat = 'woff';
      } else if (fontFamily.endsWith('.ttf')) {
        fontFormat = 'truetype';
      } else if (fontFamily.endsWith('.otf')) {
        fontFormat = 'opentype';
      } else {
        fontUrl = `${fontFamily}.otf`;
      }

      css += `
      @font-face {
        font-family: '${fontName}';
        font-weight: ${fontFaceWeight};
        src: url('https://media.usestoryteller.com/fonts/${fontUrl}') format('${fontFormat}');
      }

      .${tenantName} {
        font-family: '${fontName}', ${FALLBACK_FONTS};
      }`;
    }

    if (existingStyleTag) {
      existingStyleTag.innerHTML = css;
      return;
    }

    const styleTag = document.createElement('style');
    styleTag.id = TENANT_FONT_STYLE_TAG_ID;
    styleTag.innerHTML = css;
    document.head.appendChild(styleTag);
  }, [fontFamily, fontSize, fontWeight, tenantName]);

  const loadTenantFont = useCallback(async () => {
    if (!loadTenantSettings) {
      setIsLoadingTenantFont(false);
      return Promise.resolve();
    }

    // If the settings are loaded and there's no font family, we don't need to load anything
    if (settings && !fontFamily) {
      setIsLoadingTenantFont(false);
      return Promise.resolve();
    }

    const fontName = fontFamily.split('.')[0];
    const css = `${fontWeight || '500'} 16px '${fontName}'`;

    // Check if the font is already loaded
    if (document.fonts.check(css)) {
      setIsLoadingTenantFont(false);
      return Promise.resolve();
    }

    setIsLoadingTenantFont(true);

    await document.fonts.load(css);

    setIsLoadingTenantFont(false);

    return Promise.resolve();
  }, [fontFamily, fontWeight, loadTenantSettings, settings]);

  const context = useMemo(
    () => ({ isLoadingTenantFont, loadTenantFont }),
    [isLoadingTenantFont, loadTenantFont]
  );

  return (
    <CurrentTenantFontContext.Provider value={context}>
      {children}
    </CurrentTenantFontContext.Provider>
  );
}

export function useCurrentTenantFont() {
  const context = useContext(CurrentTenantFontContext);

  if (!context) {
    throw new Error(
      'useCurrentTenantFont must be used within a CurrentTenantFontProvider'
    );
  }

  return context;
}

export default CurrentTenantFontProvider;
